// Job actions shared by the card components.
//
// `deleteFile()` existed as three byte-identical copies (Ready, Solved and
// Error cards), and the download helpers as five near-identical sets -- with
// the same function under two different names depending on which card you
// looked at (`downloadOutputTextFile` in SolvedCard,
// `downloadOutputFile` in ErrorCard, both hitting the same endpoint).
//
// The confirm()/alert()/reload() trio is gone: confirmations are a styled
// dialog, failures are toasts, and the mutating actions return true so the
// caller can refetch its list in place rather than reloading the page and
// throwing away the tab and scroll position.

import { apiFetch } from "./api.js";
import { confirmAction } from "./confirm.js";
import { toast, dismissToast, resolveToast } from "./toast.js";

/** Downloads are plain navigations, not fetches: the browser needs to handle
 *  the Content-Disposition itself. */
export const download = {
  python: (id) => (window.location.href = `/file/downloadPythonFile/${id}`),
  inputExcel: (id) =>
    (window.location.href = `/file/downloadExcelInputFile/${id}`),
  outputExcel: (id) =>
    (window.location.href = `/file/downloadOutputExcelFile/${id}`),
  log: (id) => (window.location.href = `/file/downloadOutputTextFile/${id}`),
};

/**
 * Confirms, deletes, and reports.
 *
 * Every copy of this left `show_delete_scenario_popup` set to true on the
 * failure path, so the "Deleting, please wait" overlay stayed up forever and
 * the page looked hung. A pending toast cannot outlive its request here: the
 * failure branch dismisses it before showing the error.
 *
 * @returns {Promise<boolean>} true if the list should refresh.
 */
export async function deleteJob(id, filename) {
  const ok = await confirmAction({
    title: `Delete job #${id}?`,
    message: filename
      ? `${filename}, its Excel input and its output files are removed. This cannot be undone.`
      : "The job and all of its files are removed. This cannot be undone.",
    confirmLabel: "Delete",
    tone: "error",
  });
  if (!ok) return false;

  const pending = toast(`Deleting job #${id}…`, { tone: "pending" });
  try {
    await apiFetch(`/file/${id}`, { method: "DELETE" });
    resolveToast(pending, `Job #${id} deleted`);
    return true;
  } catch (err) {
    dismissToast(pending);
    toast(`Could not delete job #${id}: ${err.message}`, { tone: "error" });
    return false;
  }
}

/** Navigates to /queue on success -- watching the run is the reason anyone
 *  queues a job. */
export async function queueJob(id) {
  const pending = toast(`Queueing job #${id}…`, { tone: "pending" });
  try {
    await apiFetch(`/file/sendToQueue/${id}`, { method: "POST" });
    window.location.href = "/queue";
  } catch (err) {
    dismissToast(pending);
    toast(`Could not queue job #${id}: ${err.message}`, { tone: "error" });
  }
}

/** @returns {Promise<boolean>} true if the list should refresh. */
export async function dequeueJob(id) {
  const ok = await confirmAction({
    title: `Remove job #${id} from the queue?`,
    message: "It goes back to Ready and can be queued again later.",
    confirmLabel: "Remove",
  });
  if (!ok) return false;

  try {
    await apiFetch(`/file/dequeue/${id}`, { method: "POST" });
    toast(`Job #${id} removed from the queue`);
    return true;
  } catch (err) {
    toast(`Could not dequeue job #${id}: ${err.message}`, { tone: "error" });
    return false;
  }
}

export async function killJob(id) {
  const ok = await confirmAction({
    title: `Stop job #${id}?`,
    message:
      "The Python process is killed immediately and the job is marked as an error. Progress so far is lost.",
    confirmLabel: "Stop job",
    tone: "error",
  });
  if (!ok) return;

  try {
    await apiFetch(`/solver/killSolver/${id}`, { method: "DELETE" });
    toast(`Stopping job #${id}…`);
    // The Queue page polls, so the card updates itself shortly.
  } catch (err) {
    toast(`Could not stop job #${id}: ${err.message}`, { tone: "error" });
  }
}
